/**
 * Live progress events for one external test run.
 *
 * @module external-test-events
 * @description The SSE vocabulary emitted on `GET /jobs/{runId}/stream` — keyed
 * on the RUN id, not a job id, because a run is N+1 jobs (one orchestrator plus
 * one per shard), each of which reschedules itself several times as its phase
 * machine advances. The run id is the only identifier that exists before any job
 * does and never changes.
 *
 * Shared rather than declared twice for the same reason
 * `WorkflowTestStreamEvent` is: the emitter (`apps/api/src/external-tests/
 * events.ts`) and the subscriber (`apps/web`'s `useExternalRunStream`) have to
 * agree on the event names and payload fields, and a hand-mirrored copy on the
 * client drifts silently — a renamed field just stops rendering.
 */

/** Lifecycle of a run as the stream reports it. */
export type ExternalRunStreamStatus =
  | 'queued'
  | 'running'
  | 'collecting'
  | 'sealed'
  | 'canceled'
  | 'failed';

/** Where one shard is in its phase machine. */
export type ExternalShardPhase = 'queued' | 'launching' | 'running' | 'collecting' | 'done';

/** Customer-safe outcome of the current durable shard launch attempt. */
export type ExternalShardLaunchState =
  | 'timed_out'
  | 'failed'
  | 'completed'
  | 'running'
  | 'awaiting_bootstrap';

/** Durable progress embedded in the active external-run detail response. */
export type ExternalRunProgressSnapshot = {
  run: {
    status: 'open' | 'collecting' | 'sealed' | 'canceled';
    shardTotal: number | null;
    startedAt: string | null;
  };
  summary: {
    timeoutCount: number;
    /** Extra platform launches of an area (infrastructure). */
    relaunchCount: number;
    /** In-process framework retries of individual tests (not infrastructure). */
    testRetryCount: number;
  };
  shards: Array<{
    shardIndex: number;
    phase: ExternalShardPhase;
    attempt: number;
    launchState?: ExternalShardLaunchState;
  }>;
};

export type ExternalRunEvent =
  | {
      type: 'external-run.status';
      data: { runId: string; status: ExternalRunStreamStatus; emittedAt: string };
    }
  | {
      type: 'external-run.phase';
      data: {
        runId: string;
        /**
         * How many shards this run fanned out into. The UI renders exactly this
         * many chips immediately — before any of them has reported — which is
         * the whole reason the plan is its own event rather than being inferred
         * from the first shard to speak.
         */
        shardTotal: number;
        /** The commit every shard is pinned to, once resolved. */
        revision: string | null;
        targetUrl: string | null;
        emittedAt: string;
      };
    }
  | {
      type: 'external-run.shard';
      data: {
        runId: string;
        /** 1-based, matching the payload (never the artifact-key `null`). */
        shardIndex: number;
        shardTotal: number;
        phase: ExternalShardPhase;
        /** 1-based attempt within the shard; a retry is visible, not silent. */
        attempt: number;
        emittedAt: string;
      };
    }
  | {
      type: 'external-run.results';
      data: {
        runId: string;
        shardIndex: number;
        attempt: number;
        /**
         * Whether this attempt produced (or salvaged) any results at all.
         *
         * A boolean and NOT a row count, deliberately. The stream is a hint to
         * refetch, not a ledger: the run detail already computes the
         * cross-attempt verdicts, and a client that summed per-event counts
         * would drift permanently the first time one event was dropped — which
         * SSE makes routine, not exceptional.
         */
        hadResults: boolean;
        emittedAt: string;
      };
    }
  | {
      type: 'external-run.sealed';
      data: {
        runId: string;
        status: string;
        total: number;
        passed: number;
        failed: number;
        flaky: number;
        skipped: number;
        /**
         * At least one attempt salvaged incomplete results, or a shard closed
         * with no usable report at all — see `SealRunSummary` in
         * `@chatsdet/db`. Without this a viewer cannot tell a truncated
         * `total: 0` from a genuine one off this, the stream's terminal
         * event.
         */
        resultsTruncated: boolean;
        emittedAt: string;
      };
    };

/** Every event name the run stream emits — the client's `addEventListener` set. */
export const EXTERNAL_RUN_EVENT_TYPES = [
  'external-run.status',
  'external-run.phase',
  'external-run.shard',
  'external-run.results',
  'external-run.sealed',
] as const satisfies readonly ExternalRunEvent['type'][];
